import { Box, Typography, alpha, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CONTENT_SHAPE } from "../../constants/contentColors";
import { ZERO_ICONS } from "../../constants/zeroIcons";
import { contentBorders } from "../../theme/createContentTheme";

interface PilotLockedSectionProps {
  minHeight?: number;
  label?: string;
  /** Kind of feature behind the lock — only changes the icon. */
  preview?: "skeleton" | "chart" | "map";
}

/**
 * A feature that is not part of the pilot. Shown as a quiet locked panel —
 * no sample charts or invented map pins behind a blur.
 */
export function PilotLockedSection({ minHeight = 320, label, preview = "skeleton" }: PilotLockedSectionProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const borders = contentBorders(theme);
  const accessibleLabel = label ?? t("pilot.lockedTitle");
  const Icon = preview === "map" ? ZERO_ICONS.map : preview === "chart" ? ZERO_ICONS.daily : ZERO_ICONS.locked;

  return (
    <Box
      role="status"
      aria-label={accessibleLabel}
      sx={{
        minHeight,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.25,
        px: 3,
        py: 4,
        textAlign: "center",
        bgcolor: "background.paper",
        border: `1px dashed ${borders.strong}`,
        borderRadius: `${CONTENT_SHAPE.md}px`,
        backgroundImage: `repeating-linear-gradient(135deg, transparent 0 18px, ${alpha(theme.palette.text.primary, 0.025)} 18px 19px)`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
        <Icon aria-hidden sx={{ fontSize: 22 }} />
        <ZERO_ICONS.locked aria-hidden sx={{ fontSize: 18 }} />
      </Box>
      <Typography variant="subtitle1" component="p" sx={{ fontWeight: 600 }}>
        {accessibleLabel}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
        {t("pilot.lockedMessage")}
      </Typography>
    </Box>
  );
}

export default function PilotLockedView() {
  const { t } = useTranslation();
  return <PilotLockedSection minHeight={480} label={t("pilot.lockedTitle")} />;
}
